import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogIn } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface FeaturedGame {
  id: number;
  name: string;
  description: string;
  image_url: string;
  platform: string;
  price: number;
}

export default function SplashScreen() {
  const navigate = useNavigate();
  const [featuredGames, setFeaturedGames] = useState<FeaturedGame[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  
  const goToLogin = () => {
    navigate('/login');
  };

  useEffect(() => {
    const fetchFeaturedGames = async () => {
      try {
        // Fetch games with high stock that are categorized as Games
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('category', 'Games')
          .gt('stock_quantity', 5)
          .order('id', { ascending: false })
          .limit(5);

        if (error) throw error;
        
        setFeaturedGames(data as FeaturedGame[]);
      } catch (error) {
        console.error('Error fetching featured games:', error);
        // Fallback data in case the fetch fails
        setFeaturedGames([
          {
            id: 1,
            name: "Stellar Odyssey",
            description: "Embark on an epic journey through the cosmos in this breathtaking space adventure.",
            image_url: "https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?ixlib=rb-4.0.3",
            platform: "PlayStation 5",
            price: 59.99
          },
          {
            id: 2,
            name: "Forgotten Realms",
            description: "Discover ancient mysteries and battle legendary creatures in this immersive RPG.",
            image_url: "https://images.unsplash.com/photo-1511512578047-dfb367046420?ixlib=rb-4.0.3",
            platform: "Xbox Series X/S",
            price: 49.99
          },
          {
            id: 3,
            name: "Speed Demons",
            description: "Push your limits in the most exhilarating racing experience of the year.",
            image_url: "https://images.unsplash.com/photo-1511882150382-421056c89033?ixlib=rb-4.0.3",
            platform: "Nintendo Switch",
            price: 39.99
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedGames();
  }, []);

  useEffect(() => {
    // Auto-rotate the carousel
    const startAutoplay = () => {
      autoplayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prevIndex => 
          prevIndex === featuredGames.length - 1 ? 0 : prevIndex + 1
        );
        
        setTimeout(() => {
          setIsTransitioning(false);
        }, 500);
      }, 5000); // Change slide every 5 seconds
    };

    if (featuredGames.length > 0) {
      startAutoplay();
    }

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [featuredGames]);

  const goToNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prevIndex => 
      prevIndex === featuredGames.length - 1 ? 0 : prevIndex + 1
    );
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const goToPrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prevIndex => 
      prevIndex === 0 ? featuredGames.length - 1 : prevIndex - 1
    );
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div className="animate-pulse flex flex-col items-center">
          <img src="/logo-bvg-white.png" alt="Big Vision Games" className="w-16 h-auto mb-4" />
          <div className="h-2 w-32 bg-white/20 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-black"></div>
      
      {/* Logo and header */}
      <div className="absolute top-6 left-0 right-0 z-10 flex justify-center">
        <div className="flex flex-col items-center">
          <img src="/logo-bvg-white.png" alt="Big Vision Games" className="w-12 h-auto mb-2" />
          <h1 className="text-3xl text-white font-thin">BIG VISION GAMES</h1>
          <p className="text-white/70 mt-1 font-thin">Gaming Beyond Boundaries</p>
        </div>
      </div>
      
      {/* Carousel */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-full max-w-6xl h-[70vh] mx-4 overflow-hidden rounded-xl">
          {featuredGames.map((game, index) => (
            <div 
              key={game.id}
              className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                index === currentIndex 
                  ? 'opacity-100 translate-x-0 z-10' 
                  : index < currentIndex || (currentIndex === 0 && index === featuredGames.length - 1)
                    ? 'opacity-0 -translate-x-full z-0'
                    : 'opacity-0 translate-x-full z-0'
              }`}
            >
              {/* Game image with overlay */}
              <div className="absolute inset-0 bg-black/30 z-10"></div>
              <img 
                src={game.image_url} 
                alt={game.name} 
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = "https://images.unsplash.com/photo-1550745165-9bc0b252726f";
                }}
              />
              
              {/* Game details */}
              <div className="absolute bottom-0 left-0 right-0 z-20 p-8 bg-gradient-to-t from-black to-transparent">
                <div className="max-w-3xl">
                  <div className="flex items-center mb-2">
                    <span className="px-3 py-1 bg-purple-600 text-white text-sm rounded-full">
                      {game.platform}
                    </span>
                    <span className="ml-3 bg-white/10 backdrop-blur-sm px-3 py-1 text-white text-sm rounded-full">
                      £{game.price.toFixed(2)}
                    </span>
                  </div>
                  <h2 className="text-5xl font-bold text-white mb-4">{game.name}</h2>
                  <p className="text-xl text-white/80 mb-6">{game.description}</p>
                </div>
              </div>
            </div>
          ))}
          
          {/* Navigation arrows */}
          <button 
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button 
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          {/* Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
            {featuredGames.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsTransitioning(true);
                  setCurrentIndex(index);
                  setTimeout(() => {
                    setIsTransitioning(false);
                  }, 500);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white scale-125' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* CTA Button */}
      <div className="absolute bottom-16 left-0 right-0 flex justify-center z-20">
        <button
          onClick={goToLogin}
          className="px-10 py-5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 flex items-center gap-3 text-xl shadow-lg"
        >
          <LogIn className="w-6 h-6" />
          Enter the Store
        </button>
      </div>
      
      {/* Footer text */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center">
        <p className="text-white/50 text-sm">© {new Date().getFullYear()} Big Vision Games</p>
      </div>
    </div>
  );
} 
